import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { attendanceApi } from '../../../api/attendanceApi';
import { employeeApi } from '../../../api/employeeApi';
import { faceApi } from '../../../api/faceApi';
import { RootState } from '../../../store/store';

type PunchMode = 'idle' | 'capturing' | 'verifying' | 'success' | 'error';

interface PunchResult {
  timestamp: Date;
  punchType: 'CHECK_IN' | 'CHECK_OUT';
  confidence?: number;
  message: string;
  employeeName?: string;
  employeeCode?: string;
}

interface FacePunchEmployee {
  id: string;
  fullName: string;
  employeeCode: string;
}

/**
 * Face-based Punch Page
 * Allows employees to perform check-in/out using face recognition
 */
export const FacePunchPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // State
  const [mode, setMode] = useState<PunchMode>('idle');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [recentPunches, setRecentPunches] = useState<PunchResult[]>([]);
  const [todayStatus, setTodayStatus] = useState<any>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [employeeId, setEmployeeId] = useState<string | null>(user?.employeeId || null);
  const [currentEmployee, setCurrentEmployee] = useState<FacePunchEmployee | null>(null);
  const [lastSuccessfulPunch, setLastSuccessfulPunch] = useState<PunchResult | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const latestPunch = todayStatus?.todayPunches?.[todayStatus.todayPunches.length - 1];

  // Start camera
  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera is not supported in this browser');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCameraActive(true);
    } catch (error) {
      toast.error('Failed to access camera');
      console.error('Camera error:', error);
      setIsCameraActive(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setIsCameraActive(false);
  };

  // Capture and perform punch
  const performFacePunch = async (punchType: 'CHECK_IN' | 'CHECK_OUT') => {
    if (!videoRef.current || !canvasRef.current || !employeeId) return;
    if (!videoRef.current.videoWidth || !videoRef.current.videoHeight) {
      toast.warning('Camera is still starting. Please try again in a moment.');
      return;
    }

    setMode('capturing');

    try {
      // Capture image
      const context = canvasRef.current.getContext('2d');
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        const faceImageBase64 = canvasRef.current.toDataURL('image/jpeg', 0.6);

        setMode('verifying');

        // Perform punch with face verification
        try {
          const verification = await faceApi.verifyFace({
            faceImageBase64,
            tenantId: user?.tenantId,
            employeeIdHint: employeeId,
            matchThreshold: 0.78,
          });

          if (!verification.isVerified || !verification.employeeId) {
            throw new Error(verification.errorMessage || 'Face verification failed. Please try again.');
          }

          if (verification.employeeId.toLowerCase() !== employeeId.toLowerCase()) {
            throw new Error(
              `Face matched ${verification.employeeName || 'another employee'} (${verification.employeeCode || '-'}) instead of the selected employee. Attendance was not recorded.`
            );
          }

          const punchedEmployeeName = verification.employeeName || currentEmployee?.fullName || user?.fullName || 'Employee';
          const punchedEmployeeCode = verification.employeeCode || currentEmployee?.employeeCode || '';

          if (punchType === 'CHECK_IN') {
            await attendanceApi.checkInWithFace(
              employeeId,
              'Face Recognition Punch',
              faceImageBase64
            );

            const punchResult: PunchResult = {
              timestamp: new Date(),
              punchType,
              confidence: 0.95,
              message: `Checked in at ${formatTime(new Date())}`,
              employeeName: punchedEmployeeName,
              employeeCode: punchedEmployeeCode,
            };

            setRecentPunches((current) => [punchResult, ...current.slice(0, 4)]);
            setLastSuccessfulPunch(punchResult);
            setTodayStatus((current: any) => ({
              ...(current ?? {}),
              isCheckedIn: true,
              todayPunches: [
                ...((current?.todayPunches ?? []) as any[]),
                {
                  time: formatTime(new Date()),
                  type: 'CHECK IN',
                  modeOfEntry: 'Face Recognition',
                  remarks: 'Face Recognition Punch',
                },
              ],
            }));
            setMode('success');
            toast.success('Checked in successfully');

            setTimeout(() => {
              setMode('idle');
              void refreshStatus();
            }, 2000);
          } else {
            await attendanceApi.checkOutWithFace(
              employeeId,
              'Face Recognition Punch',
              faceImageBase64
            );

            const punchResult: PunchResult = {
              timestamp: new Date(),
              punchType,
              confidence: 0.95,
              message: `Checked out at ${formatTime(new Date())}`,
              employeeName: punchedEmployeeName,
              employeeCode: punchedEmployeeCode,
            };

            setRecentPunches((current) => [punchResult, ...current.slice(0, 4)]);
            setLastSuccessfulPunch(punchResult);
            setTodayStatus((current: any) => ({
              ...(current ?? {}),
              isCheckedIn: false,
              todayPunches: [
                ...((current?.todayPunches ?? []) as any[]),
                {
                  time: formatTime(new Date()),
                  type: 'CHECK OUT',
                  modeOfEntry: 'Face Recognition',
                  remarks: 'Face Recognition Punch',
                },
              ],
            }));
            setMode('success');
            toast.success('Checked out successfully');

            setTimeout(() => {
              setMode('idle');
              void refreshStatus();
            }, 2000);
          }
        } catch (error: any) {
          setMode('error');
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            'Face verification failed. Please try again.';
          toast.error(errorMessage);

          setTimeout(() => {
            setMode('idle');
          }, 3000);
        }
      }
    } catch (error) {
      setMode('error');
      toast.error('Failed to process face punch');
      setTimeout(() => {
        setMode('idle');
      }, 3000);
    }
  };

  // Refresh status
  const refreshStatus = async () => {
    if (!employeeId) return;

    try {
      const status = await attendanceApi.getTodayStatus(employeeId);
      setTodayStatus(status);
      setStatusError(null);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      setStatusError('Unable to load your attendance status.');
    }
  };

  // Load initial status
  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      setIsCheckingStatus(true);
      setStatusError(null);
      void startCamera();

      try {
        let resolvedEmployeeId = user?.employeeId || null;
        let resolvedEmployee: FacePunchEmployee | null = null;

        try {
          const employee = await employeeApi.getCurrentEmployee();
          if (employee.type !== 'Employee') {
            throw new Error('Your login is not linked to an employee profile.');
          }
          resolvedEmployee = {
            id: employee.id,
            fullName: employee.fullName,
            employeeCode: employee.employeeCode,
          };
          resolvedEmployeeId = resolvedEmployeeId || employee.id;
        } catch (error) {
          if (!resolvedEmployeeId) {
            throw error;
          }
        }

        if (cancelled) return;

        setEmployeeId(resolvedEmployeeId);
        setCurrentEmployee(resolvedEmployee);
        const status = await attendanceApi.getTodayStatus(resolvedEmployeeId);

        if (!cancelled) {
          setTodayStatus(status);
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error('Failed to load face punch status:', error);
          setTodayStatus(null);
          setStatusError(error?.response?.data?.message || error?.message || 'Unable to load your attendance status.');
        }
      } finally {
        if (!cancelled) {
          setIsCheckingStatus(false);
        }
      }
    };

    void loadStatus();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [user?.employeeId]);

  const isCheckedIn = todayStatus?.isCheckedIn;
  const canPunchIn = !isCheckingStatus && Boolean(todayStatus) && !isCheckedIn;
  const canPunchOut = !isCheckingStatus && Boolean(todayStatus) && Boolean(isCheckedIn);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2 }}>
            Face Recognition Punch
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Position your face in the camera and click the punch button
          </Typography>

          {/* Status Alert */}
          {isCheckingStatus ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Loading status...
            </Alert>
          ) : statusError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {statusError}
            </Alert>
          ) : (
            <Alert
              severity={isCheckedIn ? 'success' : 'info'}
              sx={{ mb: 3 }}
            >
              {isCheckedIn
                ? `Checked in at ${latestPunch?.time || 'Unknown'}`
                : 'Not checked in yet'}
            </Alert>
          )}

          {/* Camera Feed */}
          <Paper sx={{ bgcolor: '#000', position: 'relative', mb: 3, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 'auto',
                minHeight: 400,
                display: 'block',
                background: '#000',
              }}
            />

            {!isCameraActive && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#000',
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {/* Mode Indicator */}
            {mode !== 'idle' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.7)',
                }}
              >
                {mode === 'capturing' && (
                  <Stack alignItems="center" spacing={2}>
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography sx={{ color: 'white' }}>Capturing...</Typography>
                  </Stack>
                )}
                {mode === 'verifying' && (
                  <Stack alignItems="center" spacing={2}>
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography sx={{ color: 'white' }}>Verifying face...</Typography>
                  </Stack>
                )}
                {mode === 'success' && (
                  <Stack alignItems="center" spacing={2}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
                    <Typography sx={{ color: 'white', fontWeight: 700 }}>
                      {lastSuccessfulPunch?.punchType === 'CHECK_OUT' ? 'Checked out' : 'Checked in'} successfully
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ color: 'white', fontWeight: 700 }}>
                        {lastSuccessfulPunch?.employeeName || currentEmployee?.fullName || user?.fullName || 'Employee'}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
                        Employee Code: {lastSuccessfulPunch?.employeeCode || currentEmployee?.employeeCode || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                )}
                {mode === 'error' && (
                  <Stack alignItems="center" spacing={2}>
                    <ErrorIcon sx={{ fontSize: 60, color: '#f44336' }} />
                    <Typography sx={{ color: 'white' }}>Failed</Typography>
                  </Stack>
                )}
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'none' }}>
            <canvas ref={canvasRef} />
          </Box>

          {/* Punch Buttons */}
          <Box sx={{ mb: 4 }}>
            {isCheckedIn ? (
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                onClick={() => performFacePunch('CHECK_OUT')}
                disabled={!canPunchOut || mode !== 'idle'}
                startIcon={<CameraAltIcon />}
              >
                Check Out
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                onClick={() => performFacePunch('CHECK_IN')}
                disabled={!canPunchIn || mode !== 'idle'}
                startIcon={<CameraAltIcon />}
              >
                Check In
              </Button>
            )}
          </Box>

          {/* Recent Punches */}
          {recentPunches.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Today's Punches
              </Typography>
              <Stack spacing={1}>
                {recentPunches.map((punch, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Chip
                        label={punch.punchType === 'CHECK_IN' ? 'In' : 'Out'}
                        size="small"
                        color={punch.punchType === 'CHECK_IN' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="body2">{punch.message}</Typography>
                      {(punch.employeeName || punch.employeeCode) && (
                        <Typography variant="caption" color="textSecondary">
                          {punch.employeeName || 'Employee'} ({punch.employeeCode || '-'})
                        </Typography>
                      )}
                    </Box>
                    {punch.confidence && (
                      <Typography variant="caption" color="textSecondary">
                        {(punch.confidence * 100).toFixed(0)}% confidence
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/attendance/dashboard')}>Back to Dashboard</Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default FacePunchPage;
